import {
  SyntheticEvent,
  UIEvent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useSWRHooksConfig } from "@touchtech/web-hooks";

import {
  APP_PATH,
  LocalStorageKeys,
  ROUTES,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { ScannerContext } from "@is/contexts/ScannerContext";
import { useContentItems } from "@is/hooks";
import { useParkedBaskets } from "@is/hooks/basket/parked/useParkedBaskets";
import { useRestoreParkedBasket } from "@is/hooks/basket/parked/useRestoreParkedBasket";
import { LocalStorageService, createBasketUrl } from "@is/services";
import { SearchTerm, StoreDevice } from "@is/types";
import { getLanguageFromLocalStorage } from "@is/utils";

type Props = {
  storeInfo: StoreDevice;
};

const useManageSettingsPage = ({ storeInfo }: Props) => {
  const { t } = useTranslation(TranslationContext.Settings);
  const locale = getLanguageFromLocalStorage();
  const swrConfig = useSWRHooksConfig();
  const navigate = useNavigate();

  const externalStoreId = useMemo(() => storeInfo?.storeId || "", [storeInfo]);
  const storeName = useMemo(() => storeInfo?.name || "", [storeInfo]);
  const deviceId = LocalStorageService.getItem(LocalStorageKeys.DeviceId);

  const fullDeviceName = useMemo(() => {
    const deviceType = storeInfo?.devices[0]?.type || "";
    const deviceName = storeInfo?.devices[0]?.name || "";

    return !!deviceName?.length && !!deviceType?.length
      ? deviceName + " / " + deviceType
      : deviceType || deviceName || "";
  }, [storeInfo]);

  const {
    settings: {
      updateViewState: updateSettingsViewState,
      showResumeParkedBasketModal,
    },
    content: { updateViewState: updateContentViewState },
  } = useContext(ViewStateContext);

  const { handleSearchProducts } = useContentItems();

  const { handleSelectScanner, scannerName, handleDisconnectScanner } =
    useContext(ScannerContext);

  const { parkedBaskets } = useParkedBaskets();

  const [, restoreParkedBasket] = useRestoreParkedBasket();

  const [isScrolled, setIsScrolled] = useState(false);
  const [restoreBasketId, setRestoreBasketId] = useState<string | null>(null);
  const [restoreSessionName, setRestoreSessionName] = useState<string>("");

  const isScannerConnected = useMemo(() => !!scannerName, [scannerName]);

  const scannerDescription = useMemo(
    () =>
      isScannerConnected
        ? scannerName + " " + t("connected")
        : t("No scanner connected"),
    [isScannerConnected, scannerName, t]
  );

  const handleClose = useCallback(() => {
    updateContentViewState({ showSettingsDrawer: false });

    setTimeout(() => {
      updateSettingsViewState({
        showPasscodeView: true,
        showIFramePage: false,
      });
    }, 250);
  }, [updateContentViewState, updateSettingsViewState]);

  const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const { scrollTop } = event.currentTarget;
    setIsScrolled(scrollTop > 0);
  }, []);

  const handleIFramePageOpen = useCallback(() => {
    updateSettingsViewState({ showIFramePage: true });
  }, [updateSettingsViewState]);

  const handleConnect = useCallback(() => {
    handleSelectScanner();
  }, [handleSelectScanner]);

  const handleStoreFavoritesOpen = useCallback(() => {
    updateSettingsViewState({ showStoreFavoritesView: true });
  }, [updateSettingsViewState]);

  const handleDisconnect = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      handleDisconnectScanner();
    },
    [handleDisconnectScanner]
  );

  const handleScanner = useCallback(
    (e: SyntheticEvent) => {
      isScannerConnected ? handleDisconnect(e) : handleConnect();
    },
    [handleConnect, handleDisconnect, isScannerConnected]
  );

  const handleShowResumeParkedBasketModal = useCallback(
    (id: string, sessionName: string) => {
      updateSettingsViewState({ showResumeParkedBasketModal: true });

      setRestoreBasketId(id);
      setRestoreSessionName(sessionName);
    },
    [updateSettingsViewState]
  );

  const handleCloseResumeParkedBasketModal = useCallback(() => {
    updateSettingsViewState({ showResumeParkedBasketModal: false });

    setRestoreBasketId(null);
    setRestoreSessionName("");
  }, [updateSettingsViewState]);

  const handleResumeParkedBasket = useCallback(async () => {
    const basket = await restoreParkedBasket(restoreBasketId);

    if (basket) {
      const isEntryView =
        window.location.pathname === APP_PATH ||
        window.location.pathname === `${APP_PATH}/`;

      const searchTerms = basket.metadata.searchTerms as SearchTerm[];
      const restoredParkedBasketName =
        restoreSessionName === fullDeviceName ? "" : restoreSessionName;

      LocalStorageService.setItem(
        LocalStorageKeys.SessionId,
        basket.basket.sessionId
      );

      updateSettingsViewState({ showResumeParkedBasketModal: false });
      updateContentViewState({
        sections: [],
        showBasketDrawer: true,
        showSettingsDrawer: false,
        searchTerms,
        restoredParkedBasketName,
      });

      await handleSearchProducts({
        newSearchTerms: searchTerms as SearchTerm[],
        newSections: [],
      });

      const url = createBasketUrl();
      await swrConfig.mutate([url, basket.basket.sessionId], basket.basket);

      setRestoreBasketId(null);
      setRestoreSessionName("");

      if (isEntryView) {
        setTimeout(() => {
          navigate(`${ROUTES.ContentView}`);
        }, 1000);
      }
    }
  }, [
    handleSearchProducts,
    navigate,
    restoreBasketId,
    restoreParkedBasket,
    swrConfig,
    updateContentViewState,
    updateSettingsViewState,
    fullDeviceName,
    restoreSessionName,
  ]);

  return {
    isScrolled,
    t,
    handleClose,
    handleScroll,
    handleIFramePageOpen,
    handleStoreFavoritesOpen,
    handleScanner,
    scannerDescription,
    isScannerConnected,
    fullDeviceName,
    externalStoreId,
    deviceId,
    storeName,
    locale,
    handleShowResumeParkedBasketModal,
    showResumeParkedBasketModal,
    handleCloseResumeParkedBasketModal,
    handleResumeParkedBasket,
    parkedBaskets,
  };
};

export default useManageSettingsPage;
